import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getQueueStats, setupOfflineListener, isOnline } from '../services/offlineQueue';

/**
 * OfflineIndicator -- technician-only component that shows:
 *  1. An offline banner when connectivity is lost
 *  2. A queue badge with pending request count
 *  3. Sync status after reconnection
 *
 * Only renders for users with role === 'technician'.
 */
const OfflineIndicator = () => {
  const { user } = useAuth();
  const [offline, setOffline] = useState(!isOnline());
  const [queueCount, setQueueCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null); // null | 'syncing' | 'done' | 'failed'
  const [syncDetails, setSyncDetails] = useState({ processed: 0, failed: 0 });

  // Only render for technicians
  if (user?.role !== 'technician') {
    return null;
  }

  return (
    <OfflineIndicatorInner
      offline={offline}
      setOffline={setOffline}
      queueCount={queueCount}
      setQueueCount={setQueueCount}
      syncStatus={syncStatus}
      setSyncStatus={setSyncStatus}
      syncDetails={syncDetails}
      setSyncDetails={setSyncDetails}
    />
  );
};

/**
 * Inner component that handles the effects and rendering.
 * Separated to avoid conditional hooks.
 */
const OfflineIndicatorInner = ({
  offline,
  setOffline,
  queueCount,
  setQueueCount,
  syncStatus,
  setSyncStatus,
  syncDetails,
  setSyncDetails,
}) => {
  const refreshQueueCount = useCallback(async () => {
    try {
      const stats = await getQueueStats();
      setQueueCount(stats.total);
    } catch {
      // Ignore
    }
  }, [setQueueCount]);

  useEffect(() => {
    // Refresh queue count periodically
    refreshQueueCount();
    const interval = setInterval(refreshQueueCount, 5000);

    const cleanup = setupOfflineListener(
      () => {
        setOffline(false);
        setSyncStatus('syncing');
        // The actual queue processing is handled in api.js
        // We just update our display state
        setTimeout(async () => {
          await refreshQueueCount();
          const stats = await getQueueStats();
          if (stats.total === 0) {
            setSyncStatus('done');
            setSyncDetails({ processed: queueCount, failed: 0 });
          } else {
            setSyncStatus('failed');
            setSyncDetails({ processed: queueCount - stats.total, failed: stats.total });
          }
          // Clear sync status after a few seconds
          setTimeout(() => setSyncStatus(null), 5000);
        }, 3000);
      },
      () => {
        setOffline(true);
        setSyncStatus(null);
      }
    );

    return () => {
      cleanup();
      clearInterval(interval);
    };
  }, [refreshQueueCount, setOffline, setSyncStatus, setSyncDetails, queueCount]);

  const showBanner = offline;
  const showQueueBadge = queueCount > 0;
  const showSyncStatus = syncStatus !== null;

  if (!showBanner && !showQueueBadge && !showSyncStatus) {
    return null;
  }

  return (
    <div>
      {showBanner && (
        <div style={styles.offlineBanner}>
          <span style={styles.offlineIcon}>&#9888;</span>
          You're offline &mdash; cached data shown. Completions will sync when connected.
        </div>
      )}

      {showQueueBadge && !showBanner && (
        <div style={styles.queueBadge}>
          <span style={styles.syncIcon}>&#8635;</span>
          {queueCount} pending completion{queueCount !== 1 ? 's' : ''} queued
        </div>
      )}

      {showSyncStatus && (
        <div style={syncStatus === 'done' ? styles.syncSuccess : syncStatus === 'failed' ? styles.syncFailed : styles.syncInProgress}>
          {syncStatus === 'syncing' && 'Syncing completions...'}
          {syncStatus === 'done' && `All synced (${syncDetails.processed} completed)`}
          {syncStatus === 'failed' && `${syncDetails.failed} failed \u2014 ${syncDetails.processed} synced`}
        </div>
      )}
    </div>
  );
};

const baseBarStyle = {
  padding: '0.5rem 1rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  textAlign: 'center',
};

const styles = {
  offlineBanner: {
    ...baseBarStyle,
    background: '#fff3cd',
    color: '#856404',
    borderBottom: '1px solid #ffc107',
  },
  offlineIcon: {
    marginRight: '0.5rem',
  },
  queueBadge: {
    ...baseBarStyle,
    background: '#d1ecf1',
    color: '#0c5460',
    borderBottom: '1px solid #bee5eb',
  },
  syncIcon: {
    marginRight: '0.5rem',
    fontSize: '1rem',
  },
  syncInProgress: {
    ...baseBarStyle,
    background: '#d1ecf1',
    color: '#0c5460',
    borderBottom: '1px solid #bee5eb',
  },
  syncSuccess: {
    ...baseBarStyle,
    background: '#d4edda',
    color: '#155724',
    borderBottom: '1px solid #c3e6cb',
  },
  syncFailed: {
    ...baseBarStyle,
    background: '#f8d7da',
    color: '#721c24',
    borderBottom: '1px solid #f5c6cb',
  },
};

export default OfflineIndicator;
